import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { GeneralText } from '../firestore-models/general-text';
import { ApiGeneralText, ApiSetGeneralTextsRequest } from '../api-client-module';

import { ApiClientService } from '../api-client.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';

import { GeneralTextDialogComponent } from './general-text-dialog/general-text-dialog.component';
import { MessageDialogComponent } from '../common/message-dialog/message-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { ConfigurationService } from '../configuration.service';

@Component({
  selector: 'app-general-text',
  templateUrl: './general-text.component.html',
  styleUrls: ['./general-text.component.css'],
  standalone: false,
})
export class GeneralTextComponent implements OnInit, OnDestroy {

  //GeneralTextコレクション
  private generalTextsCollection!: AngularFirestoreCollection<GeneralText>;
  generalTextsObservable!: Observable<GeneralText[]>;
  generalTextsSubscribe!: Subscription;

  //属性情報テーブル
  displayedColumns: string[] = ['position', 'edit', 'select', 'id', 'text'];
  dataSource = new MatTableDataSource<GeneralText>();
  selection = new SelectionModel<GeneralText>(true, []);

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  projectBaseLang: string = 'ja';

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.generalTextsCollection = this.angularFirestore.collection<GeneralText>(
      'general_texts', ref => ref.orderBy('text_id', 'asc'));
    this.generalTextsObservable = this.generalTextsCollection.valueChanges({ idField: 'id' });
    this.generalTextsSubscribe = this.generalTextsObservable.subscribe(generalTexts => {
      this.dataSource.data = generalTexts;
    });
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
  }

  ngOnDestroy(): void {
    this.generalTextsSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: GeneralText): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //編集ダイアログ
  openEditDialog(generalText: GeneralText): void {
    let apiGeneralText: ApiGeneralText = {
      text_id: generalText.text_id,
      texts: generalText.texts,
    };
    const dialogRef = this.dialog.open(GeneralTextDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, generalText: apiGeneralText }
    });
  }

  //追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(GeneralTextDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, generalText: null }
    });
  }

  //削除
  deleteData(): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedGeneralTexts: Array<ApiGeneralText> = new Array<ApiGeneralText>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedGeneralText: ApiGeneralText = {
            text_id: row.text_id,
            texts: row.texts,
          };
          selectedGeneralTexts.push(selectedGeneralText);
        }
      }
      if (!!result) {
        let request: ApiSetGeneralTextsRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          general_texts: selectedGeneralTexts
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setGeneralTexts(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'));
            } else {
              this.toastr.error(this.translate.instant('削除できませんでした。'));
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

}
